import { compareMeetingsLiveFirst } from '@shared/utils/DateUtils'
import type { CalendarEvent } from '@highlight/common'

/** How long after a meeting ends it can still ride along as the back-to-back predecessor. */
const RECENTLY_ENDED_WINDOW_MS = 5 * 60 * 1000
/** Largest gap between two meetings that still counts as back-to-back. */
const BACK_TO_BACK_GAP_MS = 15 * 60 * 1000

export interface UpcomingSelection {
  /** The meeting that gets the full card — live first, else the next one up. */
  lead: CalendarEvent | null
  /** Everything else, in the order it should render as flat rows. */
  rest: Array<CalendarEvent>
}

const byStart = (a: CalendarEvent, b: CalendarEvent) => new Date(a.start).getTime() - new Date(b.start).getTime()

function endOf(event: CalendarEvent): number {
  return (event.end || event.start).getTime()
}

function areBackToBack(first: CalendarEvent, second: CalendarEvent): boolean {
  const gap = new Date(second.start).getTime() - endOf(first)
  return gap >= 0 && gap <= BACK_TO_BACK_GAP_MS
}

/**
 * Decides what Home's Upcoming section renders: one lead meeting as a full card
 * and the rest of today as flat rows (Figma `7096:18818`).
 *
 * Two rules shape the order beyond "chronological":
 *
 * 1. A meeting that ended in the last few minutes and runs back-to-back into the
 *    next one stays in the list, so a call you just left is still reachable
 *    while you hop to the next.
 * 2. The meeting being transcribed (or any happening now) floats to the top.
 *    Since only the lead renders as a card, this is also what decides which
 *    meeting gets the brief, the live transcript, and the CTAs — so it is
 *    applied last, and a just-ended meeting can never sit above a live one.
 *
 * `recordingEventId` is the event a recording is running for, which survives the
 * ended-meetings filter: a call that overruns its slot must keep its card rather
 * than vanish mid-transcript (HIG-5856). It is separate from
 * `activeTranscriptEventId` because a paused recording still holds the slot.
 *
 * `now` is injected so the windows are testable.
 */
export function selectUpcomingMeetings(
  todaysEvents: Array<CalendarEvent>,
  activeTranscriptEventId: string | null,
  now: Date,
  recordingEventId: string | null = null,
): UpcomingSelection {
  const upcoming = todaysEvents
    .filter((event) => endOf(event) > now.getTime() || event.id === recordingEventId)
    .sort(byStart)

  const meetings = [...upcoming]
  if (upcoming.length > 0) {
    const recentlyEnded = [...todaysEvents].sort(byStart).find((event) => {
      const endedAgo = now.getTime() - endOf(event)
      return endedAgo > 0 && endedAgo <= RECENTLY_ENDED_WINDOW_MS && areBackToBack(event, upcoming[0])
    })
    if (recentlyEnded) meetings.unshift(recentlyEnded)
  }

  meetings.sort(compareMeetingsLiveFirst(activeTranscriptEventId))

  const [lead = null, ...rest] = meetings
  return { lead, rest }
}

/**
 * The meetings Upcoming lists as flat rows for the day it shows. Today is
 * `selectUpcomingMeetings`, with `showsEnded` appending the ones it filtered
 * out below them rather than reordering the lot by start: the meetings still
 * to come are what the section is for, and plain start order buried them under
 * the morning's, out of sight past the row limit. Another day always lists
 * every meeting in start order, since a past day's have all ended and a future
 * day's have none to hide.
 */
export function listDayMeetings(
  daysEvents: Array<CalendarEvent>,
  {
    isToday,
    showsEnded,
    activeTranscriptEventId,
    now,
    recordingEventId = null,
  }: {
    isToday: boolean
    showsEnded: boolean
    activeTranscriptEventId: string | null
    now: Date
    recordingEventId?: string | null
  },
): Array<CalendarEvent> {
  if (!isToday) return [...daysEvents].sort(byStart)
  const { lead, rest } = selectUpcomingMeetings(daysEvents, activeTranscriptEventId, now, recordingEventId)
  const live = lead ? [lead, ...rest] : rest
  if (!showsEnded) return live
  // By id, not by end time: the selection already carries a recently-ended
  // meeting when it runs back-to-back into the next, and it keeps its place.
  const listed = new Set(live.map((meeting) => meeting.id))
  const ended = daysEvents.filter((meeting) => !listed.has(meeting.id)).sort(byStart)
  return [...live, ...ended]
}
